import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiStar, FiClock, FiDollarSign } = FiIcons;

function ThingsToDo() {
  useEffect(() => {
    // Load GetYourGuide widget script
    const script = document.createElement('script');
    script.src = 'https://widget.getyourguide.com/dist/pa.umd.production.min.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-gyg-partner-id', 'PXEGJYB');
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const highlights = [
    {
      icon: FiMapPin,
      title: "Iconic Landmarks",
      description: "Visit the Statue of Liberty, Empire State Building, and Brooklyn Bridge"
    },
    {
      icon: FiStar,
      title: "Hidden Gems",
      description: "Discover local favorites and off-the-beaten-path experiences"
    },
    {
      icon: FiClock,
      title: "Flexible Timing",
      description: "Choose from morning, afternoon, or evening activities"
    },
    {
      icon: FiDollarSign,
      title: "Best Prices",
      description: "Competitive rates with instant booking and free cancellation"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with NYC Street Background */}
      <section
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1555109307-f7d9da25c244?q=80&w=2073&auto=format&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-dark-bg"></div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Things to Do in NYC
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Explore the best of New York City with curated experiences, tours, and activities
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {highlights.map((highlight, index) => (
            <motion.div
              key={highlight.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-dark-card p-6 rounded-lg border border-dark-border"
            >
              <SafeIcon icon={highlight.icon} className="text-2xl text-accent-blue mb-4" />
              <h3 className="text-lg font-semibold mb-2">{highlight.title}</h3>
              <p className="text-gray-400 text-sm">{highlight.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-dark-card rounded-lg p-8 border border-dark-border"
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2 gradient-text">Book Your NYC Experience</h2>
            <p className="text-gray-400">Discover authentic NYC experiences curated by locals</p>
          </div>
          
          <div 
            data-gyg-href="https://widget.getyourguide.com/default/activities.frame" 
            data-gyg-location-id="59" 
            data-gyg-locale-code="en-US" 
            data-gyg-widget="activities" 
            data-gyg-number-of-items="20" 
            data-gyg-cmp="boroughtv" 
            data-gyg-partner-id="PXEGJYB"
            className="min-h-[600px] bg-white rounded-lg overflow-hidden"
          >
            <span>Powered by <a target="_blank" rel="sponsored" href="https://www.getyourguide.com/new-york-city-l59/">GetYourGuide</a></span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="bg-primary-blue/20 border border-primary-blue/30 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-2 text-accent-blue">BoroughTV's Local Recommendations</h3>
            <p className="text-gray-300">
              Every activity is carefully selected to showcase the authentic spirit of NYC. From world-famous attractions to local neighborhood tours, discover what makes each borough unique through the eyes of passionate local guides featured on BoroughTV. These experiences represent the real New York that locals love and visitors remember forever.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ThingsToDo;